import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getIncubationLogs } from '../../controllers/incubations';
import Card from '../../components/ui/Card/Card';
import './IncubationLogsScreen.css';

const IncubationLogsScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const incubationId = location.state?.incubationId;

  // State for logs data
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    total_count: 0
  });

  // Fetch logs when component mounts or incubationId changes
  useEffect(() => {
    if (incubationId) {
      fetchLogs(1);
    } else {
      setError('No incubation ID provided');
      setLoading(false);
    }
  }, [incubationId]);

  const fetchLogs = async (page = 1) => {
    if (!incubationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getIncubationLogs({
        incubation_id: incubationId,
        page: page,
        limit: 50,
        sort_direction: 'DESC'
      });

      if (response.data.success) {
        setLogs(response.data.logs || []);
        setPagination(response.data.pagination || {
          current_page: 1,
          total_pages: 1,
          has_next: false,
          has_previous: false,
          total_count: 0
        });
      } else {
        setError(response.data.error || 'Failed to fetch logs');
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Failed to fetch logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      fetchLogs(newPage);
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return timestamp;
    }
  };

  // Extract all keys and values from a potentially nested data object
  const extractKeysAndValues = (obj, result = []) => {
    if (!obj || typeof obj !== 'object') return result;

    Object.entries(obj).forEach(([key, value]) => {
      // Add the key (converted to string)
      result.push(String(key));

      // If value is an object, recursively extract from it
      if (typeof value === 'object' && value !== null) {
        extractKeysAndValues(value, result);
      } else {
        // Add the value (converted to string)
        result.push(String(value));
      }
    });

    return result;
  };

  // Convert text to title case
  const toTitleCase = (text) => {
    if (!text) return text;

    // First, handle snake_case conversion to Title Case
    let result = text.replace(/_/g, ' ');

    // Then capitalize the first letter of each word
    result = result.replace(/\b\w/g, (char) => char.toUpperCase());

    return result;
  };

  // Highlight matching substrings in summary text
  const highlightMatchingText = (summary, data) => {
    if (!summary || !data) return summary;

    let processedSummary = summary;

    // First, handle date-time format conversion
    const dateTimeRegex = /(\w{3}\s+\w{3}\s+\d{2}\s+\d{4}\s+\d{2}:\d{2}:\d{2}\s+GMT[+-]\d{4}\s+\([^)]+\))/gi;
    processedSummary = processedSummary.replace(dateTimeRegex, (match) => {
      // Extract just the date part (first 4 components)
      const parts = match.split(/\s+/);
      return parts.slice(0, 4).join(' ');
    });

    // Then, handle "Pet ID:" pattern replacement
    const petIdRegex = /Pet ID:\s*([^\s,]+)/gi;
    processedSummary = processedSummary.replace(petIdRegex, (match, petId) => {
      return `Pet ID: ${petId.toUpperCase()}`;
    });

    // Extract all keys and values from the data object for other matches
    const searchTerms = extractKeysAndValues(data);

    // Also check if the processed summary contains date patterns that should be highlighted
    const datePattern = /\b(\w{3}\s+\w{3}\s+\d{2}\s+\d{4})\b/g;
    const dateMatches = processedSummary.match(datePattern) || [];

    // Check for "Start Date" and "End Date" patterns
    const startDatePattern = /\bStart Date\b/gi;
    const endDatePattern = /\bEnd Date\b/gi;
    const startDateMatches = processedSummary.match(startDatePattern) || [];
    const endDateMatches = processedSummary.match(endDatePattern) || [];

    if (searchTerms.length === 0 && dateMatches.length === 0 && startDateMatches.length === 0 && endDateMatches.length === 0) return processedSummary;

    // Check if this is specifically a Pet ID field change
    const isPetIdField = data.field_name &&
      data.field_name.toLowerCase().includes('pet') &&
      (data.field_name.toLowerCase().includes('id') || data.field_name.toLowerCase().includes('pet'));

    // Create a regex pattern that matches any of the search terms, date patterns, or date labels (case-insensitive)
    const escapedTerms = searchTerms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const allTerms = [...escapedTerms, ...dateMatches, ...startDateMatches, ...endDateMatches];
    const regex = new RegExp(`(${allTerms.join('|')})`, 'gi');

    // Split the processed summary by the regex and highlight matching parts
    const parts = processedSummary.split(regex);

    return parts.map((part, index) => {
      // Check if this part matches any of the search terms (case-insensitive)
      const matchedTerm = searchTerms.find(term =>
        part.toLowerCase() === term.toLowerCase()
      );

      // Check if this part matches any of the date patterns
      const isDateMatch = dateMatches.some(date =>
        part.toLowerCase() === date.toLowerCase()
      );

      // Check if this part matches Start Date or End Date
      const isStartDateMatch = startDateMatches.some(date =>
        part.toLowerCase() === date.toLowerCase()
      );

      const isEndDateMatch = endDateMatches.some(date =>
        part.toLowerCase() === date.toLowerCase()
      );

      if (matchedTerm || isDateMatch || isStartDateMatch || isEndDateMatch) {
        // Skip highlighting "Field"
        if (matchedTerm && matchedTerm.toLowerCase() === 'field') {
          return part;
        }

        // Check if this is a Pet ID value that should be uppercase
        const shouldUppercase = isPetIdField && matchedTerm && (matchedTerm === data.old_value || matchedTerm === data.new_value);

        if (shouldUppercase) {
          return <strong key={index}>{toTitleCase(part)}</strong>;
        }

        return <strong key={index}>{toTitleCase(part)}</strong>;
      }

      return part;
    });
  };

  const handleBackClick = () => {
    if (incubationId) {
      navigate('/incubation', { state: { incubationId: incubationId } });
    } else {
      navigate(-1); // Go back to previous page if no incubation ID
    }
  };

  return (
    <div className="incubation-logs-screen">
      <div className="incubation-logs-header-container">
        <div className="incubation-logs-header-left">
          <button className="incubation-logs-back-button" onClick={handleBackClick}>
            <i className="fas fa-arrow-left"></i>
            <span>Back</span>
          </button>
          <div className="incubation-logs-header">
            <h1 className="incubation-logs-title">Incubation Logs</h1>
            {pagination.total_count > 0 && (
              <span className="incubation-logs-count">
                ({pagination.total_count} total logs)
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="incubation-logs-content">
        {loading && (
          <div className="incubation-logs-loading">
            <div className="loading-spinner"></div>
            <p>Loading logs...</p>
          </div>
        )}

        {error && (
          <div className="incubation-logs-error">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button 
              className="retry-button" 
              onClick={() => fetchLogs(pagination.current_page)}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && logs.length === 0 && (
          <div className="incubation-logs-empty">
            <i className="fas fa-clipboard-list"></i>
            <p>No logs found for this incubation.</p>
          </div>
        )}

        {!loading && !error && logs.length > 0 && (
          <Card className="incubation-logs-card">
            <div className="incubation-logs-table-container">
              <table className="incubation-logs-table">
                <thead>
                  <tr>
                    <th className="table-header-date">Date</th>
                    <th className="table-header-summary">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="log-table-row">
                      <td className="log-date-cell">
                        {formatTimestamp(log.created_at)}
                      </td>
                      <td className="log-summary-cell">
                        {highlightMatchingText(log.summary, log.data)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="incubation-logs-pagination">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={!pagination.has_previous}
                >
                  <i className="fas fa-chevron-left"></i>
                  Previous
                </button>
                
                <div className="pagination-info">
                  <span>
                    Page {pagination.current_page} of {pagination.total_pages}
                  </span>
                </div>
                
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={!pagination.has_next}
                >
                  Next
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default IncubationLogsScreen;
